'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { TrackViewSacItemList } from '@sola/tracking/components/datalayer.tsx';
import { useSacQueryParams } from '../../utils/query-params.sac.tsx';
import { CardList } from '../card-list/card-list.tsx';
import { FeatureButtons } from '../feature-buttons/feature-buttons.tsx';
import { SacCard } from '../sac-card/sac-card.tsx';
import { SacEmpty } from '../sac-empty/sac-empty.tsx';
import type { ResolvedSacFilters } from './sac-list.logic.ts';
import { resolveFiltersFromProps, resolveFiltersFromQueryParams, useSacList } from './sac-list.logic.ts';
import { SacListSkeleton } from './sac-list.skeleton.tsx';
type SacListLayoutProps = {
  /** When true, reads filters from URL params (dynamic), enables show-more and empty state. */
  isDynamic?: boolean;
  columns?: '1' | '2';
  columnsSm?: '1' | '2';
  columnsMd?: '1' | '2' | '3';
  columnsLg?: '1' | '2' | '3';
  getChildHref?: (child: ChildAvailabilityItem) => string;
  onChildSelect?: (child: ChildAvailabilityItem) => void;
  /** Hides assisted-selection CTAs in the results footer and empty state. */
  hideAssistedCtas?: boolean;
};
const defaultLimit = 12;
const SacListView: React.FC<{
  resolved: ResolvedSacFilters;
  isDynamic?: boolean;
} & Pick<SacListLayoutProps, 'columns' | 'columnsSm' | 'columnsMd' | 'columnsLg' | 'getChildHref' | 'onChildSelect' | 'hideAssistedCtas'>> = t0 => {
  const $ = _c(40);
  const {
    resolved,
    isDynamic: t1,
    columns: t2,
    columnsSm: t3,
    columnsMd: t4,
    columnsLg: t5,
    getChildHref,
    onChildSelect,
    hideAssistedCtas: t6
  } = t0;
  const isDynamic = t1 === undefined ? false : t1;
  const columns = t2 === undefined ? "1" : t2;
  const columnsSm = t3 === undefined ? "1" : t3;
  const columnsMd = t4 === undefined ? "2" : t4;
  const columnsLg = t5 === undefined ? "3" : t5;
  const hideAssistedCtas = t6 === undefined ? false : t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["children"];
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const {
    t
  } = useTranslation(t7);
  const {
    isLoading,
    children,
    showRichMix,
    showLoadMoreButton,
    incrementPage,
    resetFilters
  } = useSacList(resolved, isDynamic);
  if (isLoading) {
    let t8;
    if ($[1] !== resolved.limit) {
      t8 = <SacListSkeleton itemCount={resolved.limit} />;
      $[1] = resolved.limit;
      $[2] = t8;
    } else {
      t8 = $[2];
    }
    return t8;
  }
  if (children.length === 0) {
    let t8;
    if ($[3] !== hideAssistedCtas || $[4] !== isDynamic || $[5] !== resetFilters) {
      t8 = isDynamic ? <SacEmpty resetFilters={resetFilters} hideAssistedCtas={hideAssistedCtas} /> : null;
      $[3] = hideAssistedCtas;
      $[4] = isDynamic;
      $[5] = resetFilters;
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    return t8;
  }
  const t8 = Number(columns);
  const t9 = Number(columnsSm);
  const t10 = Number(columnsMd);
  const t11 = Number(columnsLg);
  let t12;
  if ($[7] !== children || $[8] !== getChildHref || $[9] !== onChildSelect) {
    let t13;
    if ($[11] !== getChildHref || $[12] !== onChildSelect) {
      t13 = child => <SacCard key={child.globalId} {...child} href={getChildHref?.(child)} onChildSelect={onChildSelect} />;
      $[11] = getChildHref;
      $[12] = onChildSelect;
      $[13] = t13;
    } else {
      t13 = $[13];
    }
    t12 = children.map(t13);
    $[7] = children;
    $[8] = getChildHref;
    $[9] = onChildSelect;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== t12 || $[17] !== t8 || $[18] !== t9) {
    t13 = <CardList columns={t8} columnsSm={t9} columnsMd={t10} columnsLg={t11} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{t12}</CardList>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t8;
    $[18] = t9;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== children) {
    t14 = <TrackViewSacItemList listId="sac" listName="Sac List" items={children} />;
    $[20] = children;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== isDynamic || $[23] !== showLoadMoreButton || $[24] !== showRichMix || $[25] !== t) {
    t15 = isDynamic && !showLoadMoreButton && <div className="w-full text-center pt-roomy-xs body-md max-w-sm mx-auto">{showRichMix && t("children:sac.useSearchControls")}{!showRichMix && t("children:sac.useClearFilters")}</div>;
    $[22] = isDynamic;
    $[23] = showLoadMoreButton;
    $[24] = showRichMix;
    $[25] = t;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== hideAssistedCtas || $[28] !== incrementPage || $[29] !== isDynamic || $[30] !== resetFilters || $[31] !== showLoadMoreButton || $[32] !== showRichMix || $[33] !== t) {
    t16 = isDynamic && <FeatureButtons variant="grow" className="w-full justify-center mt-comfy-xl md:mt-roomy-xs">{showLoadMoreButton && <ButtonStyle><button type="button" onClick={() => incrementPage()}>{t("children:sac.viewMoreChildren")}</button></ButtonStyle>}{!showLoadMoreButton && !showRichMix && <ButtonStyle variant="secondary"><button type="button" onClick={resetFilters}>{t("children:sac.clearFilters")}</button></ButtonStyle>}{!hideAssistedCtas && <ButtonStyle variant={!showLoadMoreButton && !showRichMix ? "primary" : "secondary"}><Link href="/sponsor-a-child/help-me-choose/" className="w-full md:w-fit" type="button">{t("children:sac.helpMeChoose")}</Link></ButtonStyle>}</FeatureButtons>;
    $[27] = hideAssistedCtas;
    $[28] = incrementPage;
    $[29] = isDynamic;
    $[30] = resetFilters;
    $[31] = showLoadMoreButton;
    $[32] = showRichMix;
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <>{t13}{t14}{t15}{t16}</>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};
const LiveSacList: React.FC<SacFilterProps & SacListLayoutProps> = ({
  limit = defaultLimit,
  gender,
  countries,
  ageMin,
  ageMax,
  disability,
  hiv,
  orphan,
  highlyVulnerable,
  ...layoutProps
}) => {
  const hasDefaults = gender !== undefined || countries !== undefined || ageMin !== undefined || ageMax !== undefined || disability !== undefined || hiv !== undefined || orphan !== undefined || highlyVulnerable !== undefined;
  const {
    sacFilters
  } = useSacQueryParams({
    isDynamic: true,
    defaults: hasDefaults ? {
      gender: gender as ChildGenderEnum | undefined,
      countries,
      ageMin,
      ageMax,
      disability,
      hiv,
      orphan,
      highlyVulnerable
    } : undefined
  });

  // Depend on individual fields (not the sacFilters object reference) so that
  // unrelated param changes don't recreate `resolved` and force the
  // data-fetching effect in useSacList to re-run.
  const countriesQueryKey = sacFilters.countries?.join(',');
  const filtersQueryKey = sacFilters.filters?.join(',');
  const resolved = useMemo(() => resolveFiltersFromQueryParams(sacFilters, limit), [sacFilters.gender, sacFilters.ageMin, sacFilters.ageMax, countriesQueryKey, sacFilters.birthDay, sacFilters.birthMonth, filtersQueryKey, limit]);
  return <SacListView resolved={resolved} isDynamic {...layoutProps} />;
};
const StaticSacList: React.FC<SacFilterProps & SacListLayoutProps> = ({
  columns,
  columnsSm,
  columnsMd,
  columnsLg,
  getChildHref,
  onChildSelect,
  limit,
  gender,
  ageMin,
  ageMax,
  countries,
  disability,
  hiv,
  orphan,
  highlyVulnerable
}) => {
  const countriesKey = countries?.join(',');
  const resolved = useMemo(() => resolveFiltersFromProps({
    limit,
    gender,
    ageMin,
    ageMax,
    countries,
    disability,
    hiv,
    orphan,
    highlyVulnerable
  }), [limit, gender, ageMin, ageMax, countriesKey, disability, hiv, orphan, highlyVulnerable]);
  return <SacListView resolved={resolved} columns={columns} columnsSm={columnsSm} columnsMd={columnsMd} columnsLg={columnsLg} getChildHref={getChildHref} onChildSelect={onChildSelect} />;
};
export const SacList: React.FC<SacFilterProps & SacListLayoutProps> = t0 => {
  const $ = _c(11);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      isDynamic: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const isDynamic = t1 === undefined ? false : t1;
  const limit = props.limit || defaultLimit;
  let t2;
  if ($[3] !== limit) {
    t2 = <SacListSkeleton itemCount={limit} />;
    $[3] = limit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isDynamic || $[6] !== props) {
    t3 = isDynamic ? <LiveSacList isDynamic={true} {...props} /> : <StaticSacList {...props} />;
    $[5] = isDynamic;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <Suspense fallback={t2}>{t3}</Suspense>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};